/**
 * <TutorRescheduleReasonStep> — « Pour confirmer le déplacement, indique la raison » (organism).
 *
 * L'étape `reason` d'un individuel dans l'ancien `MoveSessionReasonComponent` : la raison
 * (`rescheduling_reasons`), le message aux parents et à l'élève — obligatoire avec « Autre raison
 * (préciser) » —, l'ancien RDV barré et le nouveau, l'accord de l'élève à cocher, et l'avis « Les
 * parents seront notifiés immédiatement par SMS ». Ce qui permet de confirmer :
 * `canConfirmReschedule`, que le gabarit lit pour son bouton.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Checkbox } from "../../atoms/Checkbox"
import { Stack } from "../../atoms/containers/Stack"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { SelectField } from "../../molecules/SelectField"
import { formatDayTime, formatDuration } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UITutorRescheduleReason, type UITutorWeekSession } from "../../../ports/primary/ui/types/tutorLives"
import { newDateLabel, type TutorMoveKind, type TutorNewSlot } from "../TutorNewDateStep"

/** La raison qui rend le message obligatoire. */
const OTHER_REASON = "Autre raison (préciser)"

const isOtherReason = (reasons: UITutorRescheduleReason[], reasonId: string | null | undefined) =>
    reasons.find((reason) => reason.id === reasonId)?.label === OTHER_REASON

/** Une raison, l'accord de l'élève, et le message quand la raison l'exige : l'ancien `cantContinue`, retourné. */
export function canConfirmReschedule(input: {
    reasons: UITutorRescheduleReason[]
    reasonId?: string | null
    parentMessage: string
    studentValidated: boolean
}): boolean {
    const { reasons, reasonId, parentMessage, studentValidated } = input
    return reasonId != null && studentValidated && (!isOtherReason(reasons, reasonId) || parentMessage.trim() !== "")
}

export type TutorRescheduleReasonStepProps = {
    session: Pick<UITutorWeekSession, "date" | "duration">
    /** « le mardi à 17h00 » — l'ancien créneau régulier. */
    slotLabel: string
    moveKind: TutorMoveKind
    newDate: string
    newSlot: TutorNewSlot
    reasons: UITutorRescheduleReason[]
    reasonId?: string | null
    onSelectReason?: (id: string) => void
    parentMessage: string
    onChangeParentMessage?: (text: string) => void
    studentValidated: boolean
    onToggleStudentValidated?: () => void
    /** « Noah » — l'élève qui a validé le changement. */
    firstStudentName: string
    testID?: string
}

export function TutorRescheduleReasonStep(props: TutorRescheduleReasonStepProps) {
    const {
        session,
        slotLabel,
        moveKind,
        newDate,
        newSlot,
        reasons,
        reasonId,
        onSelectReason,
        parentMessage,
        onChangeParentMessage,
        studentValidated,
        onToggleStudentValidated,
        firstStudentName,
        testID,
    } = props
    const required = isOtherReason(reasons, reasonId)
    const what = moveKind === "one_occurrence" ? "RDV" : "créneau régulier"
    const oldLabel =
        moveKind === "one_occurrence"
            ? `${formatDayTime(session.date)} · ${formatDuration(session.duration)}`
            : `${slotLabel} · ${formatDuration(session.duration)}`

    return (
        <Stack gap="4" testID={testID}>
            <SelectField
                label="Raison"
                placeholder="Choisis une raison"
                items={reasons}
                getItemId={(reason) => reason.id}
                getItemLabel={(reason) => reason.label}
                selectedIds={reasonId != null ? [reasonId] : []}
                onChange={([id]) => id != null && onSelectReason?.(String(id))}
            />
            <TextInput
                label={`Écris un message aux parents/élève${required ? "*" : ""}`}
                value={parentMessage}
                onChangeText={(text) => onChangeParentMessage?.(text)}
                placeholder="Comme discuté ensemble, je propose de déplacer notre séance en raison de ..."
                multiline
                numberOfLines={4}
            />
            <Stack gap="1">
                <Text size="text-sm" color="text-muted">{`Ancien ${what}`}</Text>
                <Text color="text-muted" style={styles.struck}>
                    {oldLabel}
                </Text>
                <Text size="text-sm" color="text-muted">{`Nouveau ${what}`}</Text>
                <Text weight="bold" color="text-primary">
                    {newDateLabel(moveKind, newDate, newSlot, session.duration)}
                </Text>
            </Stack>
            <Checkbox
                checked={studentValidated}
                onChange={() => onToggleStudentValidated?.()}
                label={`Je confirme que j'ai déjà validé ce changement avec ${firstStudentName}, par message ou à l'oral`}
            />
            <Text size="text-xs" weight="bold" color="text-muted" center>
                Les parents seront notifiés immédiatement par SMS
            </Text>
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    struck: { textDecorationLine: "line-through" },
}))
